"use client";

import { getProductDetailsFromElement } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/__internal__/getProductDetailsFromElement";
import { IntersectionObserverProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/__internal__/ProductTileViewImpressionContext";
import { UnmountProductTileProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/__internal__/UnmountProductTileContext";
import { ProductTileViewImpressionUpperThresholdProvider } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/__internal__/ViewportUpperThreshold";
import { useVisibilityChangeEvent } from "@faire/deprecated-retailer-visitor-shared/hooks/useVisibilityChangeEvent";
import { getIsRobot } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getIsRobot";
import { trackProductTileTileView } from "@faire/web-api--source/events/productTile/view/tile";
import { Surface } from "@faire/web-api--source/faire/logging/data/Surface";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import React, { PropsWithChildren, useCallback, useRef, useState } from "react";

export interface ITrackProductTileProviderProps {
  context:
    | `${keyof typeof ProductTileContainer}|${string}`
    | keyof typeof ProductTileContainer;
  requestId: string | undefined;
  screenContext: `${keyof typeof Surface}`;
  containerContext: `${keyof typeof ProductTileScreenContainer}`;
  contextToken?: string;
  productTileContainer?: ProductTileContainer;
  containerName?: string;
}

type IDwellTimerMap = Record<
  string,
  {
    start: number;
    maxThreshold: number;
    promotedBy: string;
    impressionId: string;
  }
>;

export const ProductTileViewImpressionProvider: React.FC<
  PropsWithChildren<ITrackProductTileProviderProps>
> = ({
  children,
  context,
  requestId,
  screenContext,
  containerContext,
  contextToken,
}) => {
  const [viewportThresholdHeightFromTop, setViewportThresholdHeightFromTop] =
    useState<number>(0);

  const rootMargin = `${-viewportThresholdHeightFromTop}px 0px 0px 0px`;

  const dwellTimerMapRef = useRef<IDwellTimerMap>({});

  const handleProductTileUnmount = useCallback(
    (productToken: string, impressionId: string) => {
      reportDwellTimerForProductTile(
        productToken,
        context,
        requestId ?? "",
        dwellTimerMapRef,
        impressionId,
        screenContext,
        containerContext,
        contextToken
      );
    },
    [containerContext, context, requestId, screenContext, contextToken]
  );

  useVisibilityChangeEvent((visibilityState) => {
    if (visibilityState === "hidden") {
      const productTokens = Object.keys(dwellTimerMapRef.current);
      productTokens.forEach((productToken) => {
        const impressionId =
          dwellTimerMapRef.current[productToken]?.impressionId ?? "";
        reportDwellTimerForProductTile(
          productToken,
          context,
          requestId ?? "",
          dwellTimerMapRef,
          impressionId,
          screenContext,
          containerContext,
          contextToken
        );
      });
    }
  });

  const handleIntersectionObserverCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const { productToken, promotedBy, impressionId } =
          getProductDetailsFromElement(entry.target);
        if (entry.isIntersecting && !dwellTimerMapRef.current[productToken]) {
          dwellTimerMapRef.current[productToken] = {
            start: Date.now(),
            maxThreshold: entry.intersectionRatio,
            promotedBy,
            impressionId,
          };
        } else if (
          !entry.isIntersecting &&
          dwellTimerMapRef.current[productToken]
        ) {
          reportDwellTimerForProductTile(
            productToken,
            context,
            requestId ?? "",
            dwellTimerMapRef,
            impressionId,
            screenContext,
            containerContext,
            contextToken
          );
        } else if (
          entry.isIntersecting &&
          dwellTimerMapRef.current[productToken]
        ) {
          if (
            (dwellTimerMapRef.current[productToken]?.maxThreshold ?? 0) <
            entry.intersectionRatio
          ) {
            dwellTimerMapRef.current[productToken]!.maxThreshold =
              entry.intersectionRatio;
          }
        }
      });
    },
    [containerContext, context, requestId, screenContext, contextToken]
  );

  if (getIsRobot()) {
    return children;
  }

  return (
    <UnmountProductTileProvider onUnmount={handleProductTileUnmount}>
      <ProductTileViewImpressionUpperThresholdProvider
        heightFromTop={viewportThresholdHeightFromTop}
        setHeightFromTop={setViewportThresholdHeightFromTop}
      >
        <IntersectionObserverProvider
          callback={handleIntersectionObserverCallback}
          options={{
            threshold: TWO_PERCENT_THRESHOLD,
            rootMargin,
          }}
        >
          {children}
        </IntersectionObserverProvider>
      </ProductTileViewImpressionUpperThresholdProvider>
    </UnmountProductTileProvider>
  );
};

const reportDwellTimerForProductTile = (
  productToken: string,
  context: string,
  requestId: string,
  dwellTimerMapRef: React.RefObject<IDwellTimerMap>,
  impressionId: string,
  screenContext?: string,
  containerContext?: string,
  contextToken?: string
) => {
  if (dwellTimerMapRef.current[productToken]) {
    const { start, maxThreshold, promotedBy } =
      dwellTimerMapRef.current[productToken]!;
    const stop = Date.now();

    trackProductTileTileView(
      context,
      requestId ?? "",
      productToken,
      promotedBy,
      (stop - start).toString(),
      maxThreshold.toString() ?? "",
      impressionId,
      screenContext,
      containerContext,
      contextToken,
      {
        clientCreatedAt: start,
      }
    );

    delete dwellTimerMapRef.current[productToken];
  }
};

const TWO_PERCENT_THRESHOLD = new Array(51)
  .fill(0)
  .map((_, index) => index * 2)
  // this ensures no 0.15000000001 values
  .map((percent) => percent / 100);
